import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../util/firebase";
import { query, collection, getDocs, where } from "firebase/firestore";

import { logout } from "../../util/firebase";

import { Button } from "reactstrap";
// Avatar decoration resolved inside Avatar component
import Avatar from "../../components/Avatar/Avatar";

import Swal from "sweetalert2";

import BarLoader from "react-spinners/BarLoader";

import { useTranslation } from "react-i18next";

// O(1) indexes - Memoized for performance
const buildGroupIndex = (groups) => {
  const m = new Map();
  (groups || []).forEach((g) => m.set(g.uid, g));
  return m;
};

const buildDriverIndex = (group) => {
  // result -> driverName (for RACING)
  const m = new Map();
  (group?.drivers || []).forEach((d) => m.set(d.result, d.name));
  return m;
};

const buildEventResultIndex = (group) => {
  // `${home}|${away}` -> result (for team sports)
  const m = new Map();
  (group?.events || []).forEach((e) =>
    m.set(`${e.homeTeam}|${e.awayTeam}`, e.result)
  );
  return m;
};

// July is month 6 in Date.UTC (zero-based months)
const THRESHOLD_MS = Date.UTC(2025, 6, 1); // 2025-07-01T00:00:00.000Z

const toMillis = (v) => {
  if (!v) return NaN;
  // Firestore Timestamp: has toMillis or seconds/nanoseconds
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number")
    return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  if (typeof v === "number") return v; // already millis
  if (typeof v === "string") {
    // Avoid Safari’s non-ISO parsing; try ISO first, else Date.parse as fallback
    // If your strings are ISO (recommended), this is safe.
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? NaN : ms;
  }
  // Last resort
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? NaN : ms;
};

const isEligibleGroup = (group) => {
  // We’ll keep your original check that requires these fields to exist
  if (!group?.dateCreated || !group?.openingDateTime) return false;

  const openMs = toMillis(group.openingDateTime);
  if (Number.isNaN(openMs)) return false;

  // If the intent is "Skip groups created after July 1, 2025",
  // then EXCLUDE anything ON or AFTER the threshold:
  return openMs >= THRESHOLD_MS;
};

const useErrorToast = () => {
  const lastToastAtRef = useRef(0);
  return (title) => {
    const now = Date.now();
    // Throttle to 1 toast per 2 seconds
    if (now - lastToastAtRef.current < 2000) return;
    lastToastAtRef.current = now;

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "error",
      title,
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
    });
  };
};

function AccountPage() {
  const { t } = useTranslation("common");

  const [user] = useAuthState(auth);

  const navigate = useNavigate();

  const errorToast = useErrorToast();

  // Separate loading states for better UX - allows progressive rendering
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPointsLoading, setIsPointsLoading] = useState(false);
  const [isRankLoading, setIsRankLoading] = useState(false);

  // eslint-disable-next-line
  const [userData, setUserData] = useState();
  const [userChampionTier, setUserChampionTier] = useState(null);
  // NEW MEMBERSHIP BASED BADGES
  const [activeMembershipTier, setActiveMembershipTier] = useState(null);
  //

  // Memoize tier calculation
  const calculateChampionTier = useCallback((championPoints) => {
    if (championPoints >= 900) return "TIER 10";
    if (championPoints >= 800) return "TIER 9";
    if (championPoints >= 700) return "TIER 8";
    if (championPoints >= 600) return "TIER 7";
    if (championPoints >= 500) return "TIER 6";
    if (championPoints >= 400) return "TIER 5";
    if (championPoints >= 300) return "TIER 4";
    if (championPoints >= 200) return "TIER 3";
    if (championPoints >= 100) return "TIER 2";
    return "TIER 1";
  }, []);

  const fetchUserData = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const doc = await getDocs(q);
      const data = doc.docs[0].data();
      setUserData(data);

      // NEW MEMBERSHIP BASED BADGES
      setActiveMembershipTier(data.membershipTier);
      //

      setUserChampionTier(calculateChampionTier(data.championPoints || 0));
    } catch (err) {
      console.error(err);
      errorToast("Error fetching user data");
    }
  };

  // Initial data fetch on mount - this is critical and blocking
  useEffect(() => {
    if (user) {
      fetchUserData().finally(() => {
        setIsInitialLoading(false); // Allow page to render once user data loads
      });
    }
    // eslint-disable-next-line
  }, [user]);

  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const fetchNotifications = async () => {
    if (!user) return;
    try {
      // Query only unread notifications - much faster
      const q = query(
        collection(db, "notifications"),
        where("recipientUid", "==", user?.uid),
        where("isRead", "==", false)
      );
      const docs = await getDocs(q);
      // Deduplicate by content to get accurate unread count
      const seenKeys = new Set();
      let uniqueCount = 0;
      docs.forEach((doc) => {
        const n = doc.data();
        const key = `${n.title}|${n.description}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueCount++;
        }
      });
      setUnreadNotifications(uniqueCount);
    } catch (err) {
      console.error(err);
      errorToast("Error fetching notifications");
    }
  };

  // Non-blocking fetch - runs after initial render
  useEffect(() => {
    if (user && !isInitialLoading) {
      fetchNotifications();
    }
    // eslint-disable-next-line
  }, [user, isInitialLoading]);

  const [achievementsUnlocked, setAchievementsUnlocked] = useState(0);
  const fetchAchievements = async () => {
    if (!user) return;
    try {
      // PERFORMANCE: Filter achievements at query level if possible
      // If Firestore doesn't support array-contains on achieverUids efficiently,
      // consider storing this count in the user document instead
      const q = query(
        collection(db, "achievements"),
        where("achieverUids", "array-contains", user.uid)
      );
      const docs = await getDocs(q);
      setAchievementsUnlocked(docs.size);
    } catch (err) {
      console.error(err);
      errorToast("Error fetching achievements");
    }
  };

  // Non-blocking fetch
  useEffect(() => {
    if (user && !isInitialLoading) {
      fetchAchievements();
    }
    // eslint-disable-next-line
  }, [user, isInitialLoading]);

  const [challenges, setChallenges] = useState([]);
  const fetchChallenges = async () => {
    if (!user) return;
    try {
      // Only fetch active rooms where user is a member
      const q = query(
        collection(db, "rooms"),
        where("memberUids", "array-contains", user.uid),
        where("isActive", "==", true)
      );
      const docs = await getDocs(q);

      let fetchedActiveChallenges = [];
      docs.forEach((doc) => {
        const newRoom = doc.data();
        if (newRoom.challenges) {
          newRoom.challenges.forEach((challenge) => {
            if (
              !challenge.isCompleted &&
              challenge.bets?.some((bet) => bet.bettorUid === user.uid)
            ) {
              fetchedActiveChallenges.push(challenge);
            }
          });
        }
      });

      setChallenges(fetchedActiveChallenges);
    } catch (err) {
      console.error(err);
      errorToast("Error fetching challenges");
    }
  };

  // Non-blocking fetch
  useEffect(() => {
    if (user && !isInitialLoading) {
      fetchChallenges();
    }
    // eslint-disable-next-line
  }, [user, isInitialLoading]);

  const [friends, setFriends] = useState([]);

  // Wallet history moved to its own page (`/account/wallet`)

  const fetchUserRelationships = async () => {
    if (!user) return;
    try {
      // Query only accepted relationships
      const q = query(
        collection(db, "userRelationships"),
        where("users", "array-contains", user?.uid),
        where("status", "==", "accepted")
      );
      const doc = await getDocs(q);
      setFriends(doc.docs.map((doc) => doc.data()));
    } catch (err) {
      console.error(err);
      errorToast("Error fetching friends");
    }
  };

  // Non-blocking fetch
  useEffect(() => {
    if (user && !isInitialLoading) {
      fetchUserRelationships();
    }
    // eslint-disable-next-line
  }, [user, isInitialLoading]);

  // wallet history now lives at /account/wallet

  const logoutUser = () => {
    Swal.fire({
      text: t("areYouSureYouWantToSignOut"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#005EE1",
      confirmButtonText: t("yesSignOut").toUpperCase(),
      cancelButtonText: t("noCancel").toUpperCase(),
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };

  const [groups, setGroups] = useState([]);

  // Memoize group index to avoid rebuilding on every calculation
  const groupIndex = useMemo(() => buildGroupIndex(groups), [groups]);

  // Fetch only groups needed for calculations (eligible groups after July 2025)
  const fetchGroups = async () => {
    try {
      // CRITICAL OPTIMIZATION: Only fetch recent/relevant groups
      // If you have a lot of historical groups, add date filtering here
      // For now, fetch all but process asynchronously
      const q = query(collection(db, "groups"));
      const docs = await getDocs(q);
      const fetchedGroups = docs.docs.map((doc) => doc.data());
      setGroups(fetchedGroups);
    } catch (err) {
      console.error(err);
      errorToast("Error fetching groups");
    }
  };

  // Fetch groups in background - not blocking initial render
  useEffect(() => {
    if (!isInitialLoading) {
      fetchGroups();
    }
    // eslint-disable-next-line
  }, [isInitialLoading]);

  function compareByPoints(a, b) {
    return b.total - a.total;
  }

  const getRoomMembers = (room) => {
    let fetchedRoomMembers = [...room.members];

    fetchedRoomMembers.forEach((member) => {
      let challengesWon = 0;
      let points = 0;
      let starPoints = 0;
      let total = 0;

      room?.submissions?.forEach((submission) => {
        if (submission.submitterUid === member.uid) {
          const group = groups.find((e) => e.uid === submission.groupUid);
          if (!isEligibleGroup(group)) return;
          let userChallengesWon = room?.challenges?.filter((e) =>
            e.bets.some(
              (b) => b.bettorUid === member.uid && b.result === e.result
            )
          ).length
            ? room?.challenges?.filter((e) =>
                e.bets.some(
                  (b) => b.bettorUid === member.uid && b.result === e.result
                )
              ).length
            : 0;

          let userPoints = 0;
          let userStarPoints = 0;

          if (room?.leagueSport === "RACING") {
            // console.log(submission?.positions.firstPolePositionDriver, group?.drivers.find(e => e.result === 'firstPolePosition')?.name)
            if (
              submission?.positions.firstPolePositionDriver ===
                group?.drivers.find((e) => e.result === "firstPolePosition")
                  ?.name ||
              submission?.positions.firstPolePositionDriver ===
                group?.drivers.find((e) => e.result === "secondPolePosition")
                  ?.name ||
              submission?.positions.firstPolePositionDriver ===
                group?.drivers.find((e) => e.result === "thirdPolePosition")
                  ?.name
            ) {
              userStarPoints += 2;
            }
            if (
              submission?.positions.secondPolePositionDriver ===
                group?.drivers.find((e) => e.result === "firstPolePosition")
                  ?.name ||
              submission?.positions.secondPolePositionDriver ===
                group?.drivers.find((e) => e.result === "secondPolePosition")
                  ?.name ||
              submission?.positions.secondPolePositionDriver ===
                group?.drivers.find((e) => e.result === "thirdPolePosition")
                  ?.name
            ) {
              userStarPoints += 2;
            }
            if (
              submission?.positions.thirdPolePositionDriver ===
                group?.drivers.find((e) => e.result === "firstPolePosition")
                  ?.name ||
              submission?.positions.thirdPolePositionDriver ===
                group?.drivers.find((e) => e.result === "secondPolePosition")
                  ?.name ||
              submission?.positions.thirdPolePositionDriver ===
                group?.drivers.find((e) => e.result === "thirdPolePosition")
                  ?.name
            ) {
              userStarPoints += 2;
            }
            if (
              submission?.positions.fourthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fourthPlace")?.name ||
              submission?.positions.fourthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fifthPlace")?.name ||
              submission?.positions.fourthPlaceDriver ===
                group?.drivers.find((e) => e.result === "sixthPlace")?.name ||
              submission?.positions.fourthPlaceDriver ===
                group?.drivers.find((e) => e.result === "seventhPlace")?.name ||
              submission?.positions.fourthPlaceDriver ===
                group?.drivers.find((e) => e.result === "eighthPlace")?.name
            ) {
              userPoints += 1;
            }
            if (
              submission?.positions.fifthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fourthPlace")?.name ||
              submission?.positions.fifthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fifthPlace")?.name ||
              submission?.positions.fifthPlaceDriver ===
                group?.drivers.find((e) => e.result === "sixthPlace")?.name ||
              submission?.positions.fifthPlaceDriver ===
                group?.drivers.find((e) => e.result === "seventhPlace")?.name ||
              submission?.positions.fifthPlaceDriver ===
                group?.drivers.find((e) => e.result === "eighthPlace")?.name
            ) {
              userPoints += 1;
            }
            if (
              submission?.positions.sixthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fourthPlace")?.name ||
              submission?.positions.sixthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fifthPlace")?.name ||
              submission?.positions.sixthPlaceDriver ===
                group?.drivers.find((e) => e.result === "sixthPlace")?.name ||
              submission?.positions.sixthPlaceDriver ===
                group?.drivers.find((e) => e.result === "seventhPlace")?.name ||
              submission?.positions.sixthPlaceDriver ===
                group?.drivers.find((e) => e.result === "eighthPlace")?.name
            ) {
              userPoints += 1;
            }
            if (
              submission?.positions.seventhPlaceDriver ===
                group?.drivers.find((e) => e.result === "fourthPlace")?.name ||
              submission?.positions.seventhPlaceDriver ===
                group?.drivers.find((e) => e.result === "fifthPlace")?.name ||
              submission?.positions.seventhPlaceDriver ===
                group?.drivers.find((e) => e.result === "sixthPlace")?.name ||
              submission?.positions.seventhPlaceDriver ===
                group?.drivers.find((e) => e.result === "seventhPlace")?.name ||
              submission?.positions.seventhPlaceDriver ===
                group?.drivers.find((e) => e.result === "eighthPlace")?.name
            ) {
              userPoints += 1;
            }
            if (
              submission?.positions.eighthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fourthPlace")?.name ||
              submission?.positions.eighthPlaceDriver ===
                group?.drivers.find((e) => e.result === "fifthPlace")?.name ||
              submission?.positions.eighthPlaceDriver ===
                group?.drivers.find((e) => e.result === "sixthPlace")?.name ||
              submission?.positions.eighthPlaceDriver ===
                group?.drivers.find((e) => e.result === "seventhPlace")?.name ||
              submission?.positions.eighthPlaceDriver ===
                group?.drivers.find((e) => e.result === "eighthPlace")?.name
            ) {
              userPoints += 1;
            }
            if (submission?.positions.fastestLapDriver === group?.fastestLap) {
              userPoints += 1;
            }
          } else {
            submission?.events.forEach((event) => {
              if (
                event.selectedResult ===
                group?.events.find(
                  (e) =>
                    e.homeTeam === event.homeTeam &&
                    e.awayTeam === event.awayTeam
                )?.result
              ) {
                if (event.isStarGame) {
                  if (
                    room.leagueName === "NBA" ||
                    room.leagueName === "MLB" ||
                    room.leagueName === "NHL"
                  ) {
                    userStarPoints += 1;
                  } else if (
                    room.leagueName === "NFL" ||
                    room.leagueName === "UFL"
                  ) {
                    userStarPoints += 4;
                  } else {
                    userStarPoints += 6;
                  }
                } else {
                  if (
                    room.leagueName === "NBA" ||
                    room.leagueName === "MLB" ||
                    room.leagueName === "NHL"
                  ) {
                    userPoints += 0.25;
                  } else if (
                    room.leagueName === "NFL" ||
                    room.leagueName === "UFL"
                  ) {
                    userPoints += 2;
                  } else {
                    userPoints += 3;
                  }
                }
              } else if (
                group?.events.find(
                  (e) =>
                    e.homeTeam === event.homeTeam &&
                    e.awayTeam === event.awayTeam
                )?.result === "Draw" &&
                (event.selectedResult === "Home" ||
                  event.selectedResult === "Away")
              ) {
                if (event.isStarGame) {
                  userStarPoints += 2;
                } else {
                  userPoints += 1;
                }
              }
            });
          }

          challengesWon = userChallengesWon;
          points += userPoints;
          starPoints += userStarPoints;
          total += userPoints + userStarPoints;
        }
      });

      member.challengesWon = challengesWon;
      member.points = points;
      member.starPoints = starPoints;
      member.total = total;
      // if (member.name === "Jose Miguel" && roomUid === "5") {
      //   member.total += 71;
      // }
    });

    fetchedRoomMembers.sort(compareByPoints);
    fetchedRoomMembers.forEach((member, index) => {
      member.place = index + 1;
    });

    return fetchedRoomMembers;
  };

  const [userRank, setUserRank] = useState(null);

  const fetchLeaderboardTopFifteen = async () => {
    if (!userData?.uid) return;
    try {
      setIsRankLoading(true);

      // OPTIMIZATION: Only fetch rooms where calculations are needed
      // Consider caching or pre-computing leaderboards server-side
      const roomsQuery = query(collection(db, "rooms"));
      const roomsSnapshot = await getDocs(roomsQuery);
      const rooms = roomsSnapshot.docs.map((doc) => doc.data());

      const userMap = {};

      // Process rooms in batches to avoid blocking UI
      for (const room of rooms) {
        const roomLeaderboard = getRoomMembers(room);
        roomLeaderboard.forEach((member) => {
          if (!userMap[member.uid]) {
            userMap[member.uid] = {
              ...member,
              points: 0,
              starPoints: 0,
              total: 0,
              challengesWon: 0,
            };
          }
          userMap[member.uid].points += member.points || 0;
          userMap[member.uid].starPoints += member.starPoints || 0;
          userMap[member.uid].total += member.total || 0;
          userMap[member.uid].challengesWon += member.challengesWon || 0;
        });
      }

      const aggregatedLeaderboard = Object.values(userMap).sort(
        (a, b) => b.total - a.total
      );

      const newUserRank = aggregatedLeaderboard.findIndex(
        (user) => user.uid === userData?.uid
      );

      setUserRank(newUserRank !== -1 ? newUserRank + 1 : null);
    } catch (err) {
      console.error(err);
      errorToast("Error fetching leaderboard");
    } finally {
      setIsRankLoading(false);
    }
  };

  // Debounce expensive leaderboard calculation
  useEffect(() => {
    if (Array.isArray(groups) && groups.length > 0 && userData?.uid) {
      // Use setTimeout to defer this heavy calculation
      const timer = setTimeout(() => {
        fetchLeaderboardTopFifteen();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [groups, userData?.uid]);

  const [totalChampionPoints, setTotalChampionPoints] = useState(0);

  const fetchTotalChampionPoints = useCallback(async () => {
    if (!user) return;
    if (groups.length === 0) return;

    try {
      setIsPointsLoading(true);

      // Use memoized groupIndex, build caches once
      const driverIndexCache = new Map(); // groupUid -> Map(result -> driverName)
      const eventResultIndexCache = new Map(); // groupUid -> Map(key -> result)

      // Fetch only rooms where the user participates (from step 1)
      const roomsQ = query(
        collection(db, "rooms"),
        where("memberUids", "array-contains", user.uid)
      );
      const roomsSnapshot = await getDocs(roomsQ);
      const rooms = roomsSnapshot.docs.map((d) => d.data());

      let userPoints = 0;
      let userStarPoints = 0;

      for (const room of rooms) {
        if (!Array.isArray(room?.submissions) || room.submissions.length === 0)
          continue;

        const leagueName = room?.leagueName;
        const isRacing = room?.leagueSport === "RACING";

        for (const submission of room.submissions) {
          if (submission?.submitterUid !== user.uid) continue;

          const group = groupIndex.get(submission.groupUid);
          if (!isEligibleGroup(group)) continue;

          if (isRacing) {
            // Build or reuse driver index
            let driverByResult = driverIndexCache.get(group.uid);
            if (!driverByResult) {
              driverByResult = buildDriverIndex(group);
              driverIndexCache.set(group.uid, driverByResult);
            }

            const pos = submission?.positions || {};

            // Star positions: match any of first/second/third pole winners
            const poleWinners = [
              driverByResult.get("firstPolePosition"),
              driverByResult.get("secondPolePosition"),
              driverByResult.get("thirdPolePosition"),
            ];

            if (
              pos.firstPolePositionDriver &&
              poleWinners.includes(pos.firstPolePositionDriver)
            )
              userStarPoints += 2;
            if (
              pos.secondPolePositionDriver &&
              poleWinners.includes(pos.secondPolePositionDriver)
            )
              userStarPoints += 2;
            if (
              pos.thirdPolePositionDriver &&
              poleWinners.includes(pos.thirdPolePositionDriver)
            )
              userStarPoints += 2;

            // Places 4..8 worth 1 point if matches any of results 4..8
            const placeChecks = [
              [
                "fourthPlaceDriver",
                [
                  "fourthPlace",
                  "fifthPlace",
                  "sixthPlace",
                  "seventhPlace",
                  "eighthPlace",
                ],
              ],
              [
                "fifthPlaceDriver",
                [
                  "fourthPlace",
                  "fifthPlace",
                  "sixthPlace",
                  "seventhPlace",
                  "eighthPlace",
                ],
              ],
              [
                "sixthPlaceDriver",
                [
                  "fourthPlace",
                  "fifthPlace",
                  "sixthPlace",
                  "seventhPlace",
                  "eighthPlace",
                ],
              ],
              [
                "seventhPlaceDriver",
                [
                  "fourthPlace",
                  "fifthPlace",
                  "sixthPlace",
                  "seventhPlace",
                  "eighthPlace",
                ],
              ],
              [
                "eighthPlaceDriver",
                [
                  "fourthPlace",
                  "fifthPlace",
                  "sixthPlace",
                  "seventhPlace",
                  "eighthPlace",
                ],
              ],
            ];

            for (const [field, results] of placeChecks) {
              const pick = pos[field];
              if (!pick) continue;
              for (const res of results) {
                if (pick === driverByResult.get(res)) {
                  userPoints += 1;
                  break;
                }
              }
            }

            if (pos.fastestLapDriver === group?.fastestLap) {
              userPoints += 1;
            }
          } else {
            // Team sports: build or reuse event result index
            let resultIndex = eventResultIndexCache.get(group.uid);
            if (!resultIndex) {
              resultIndex = buildEventResultIndex(group);
              eventResultIndexCache.set(group.uid, resultIndex);
            }

            for (const ev of submission?.events || []) {
              const actual = resultIndex.get(`${ev.homeTeam}|${ev.awayTeam}`);
              const isStar = !!ev.isStarGame;

              if (ev.selectedResult === actual) {
                if (["NBA", "MLB", "NHL"].includes(leagueName)) {
                  if (isStar) userStarPoints += 1;
                  else userPoints += 0.25;
                } else if (["NFL", "UFL"].includes(leagueName)) {
                  if (isStar) userStarPoints += 4;
                  else userPoints += 2;
                } else {
                  if (isStar) userStarPoints += 6;
                  else userPoints += 3;
                }
              } else if (
                actual === "Draw" &&
                (ev.selectedResult === "Home" || ev.selectedResult === "Away")
              ) {
                if (isStar) userStarPoints += 2;
                else userPoints += 1;
              }
            }
          }
        }
      }

      setTotalChampionPoints(userPoints + userStarPoints);
      setIsPointsLoading(false);
    } catch (err) {
      console.error(err);
      errorToast("Error calculating champion points");
    } finally {
      setIsPointsLoading(false);
    }
    // eslint-disable-next-line
  }, [user, groupIndex]);

  // Debounce champion points calculation
  useEffect(() => {
    if (user && groups.length > 0) {
      const timer = setTimeout(() => {
        fetchTotalChampionPoints();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [user, groups.length]);

  const getActiveMembershipTierIcon = (tier) => {
    switch (tier) {
      case "platinum":
        return "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fbadges%2FMultiplicador%202x%20Platinum.png?alt=media&token=41617020-aeb0-44dc-9d4f-5c32bc47b9df";
      case "gold":
        return "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fbadges%2FMultiplicador%202x%20Gold.png?alt=media&token=90bc68dd-b55c-4ad9-bfd8-5ef9a1a9d192";
      default:
        return null;
    }
  };

  // Only show loading for initial critical data (user info)
  if (isInitialLoading || !userData)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{
            width: 100,
            marginBottom: "1rem",
          }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="accountPage" className="page">
      <img
        onClick={() => navigate("/account/profile")}
        alt="bannerImage"
        src={userData?.bannerDesktop}
        style={{ width: "100%", borderRadius: 20, cursor: "pointer" }}
        className="hideOnMobile"
      />
      <img
        onClick={() => navigate("/account/profile")}
        alt="bannerImage"
        src={userData?.bannerMobile}
        style={{ width: "100%", borderRadius: 20, cursor: "pointer" }}
        className="hideOnDesktop"
      />
      <div
        onClick={() => navigate("/account/profile")}
        className="underProfileBannerContainer"
      >
        <Avatar
          alt={userData?.displayName || "avatar"}
          src={userData?.avatar}
          size={168}
          user={userData}
          className="underProfileAvatar"
        />
        <div
          className="underProfileBannerTextContainer"
          style={{ marginLeft: "1.5rem" }}
        >
          {/* Sports Pass feature removed; membership visuals handled by Avatar */}
          <p
            id="accountPageUsername"
            style={{
              color: "#FFFFFF",
              marginBottom: 0,
              fontSize: 32,
              fontWeight: 700,
            }}
          >
            {getActiveMembershipTierIcon(activeMembershipTier) && (
              <img
                alt="badge"
                src={getActiveMembershipTierIcon(activeMembershipTier)}
                style={{
                  width: 50,
                  height: "auto",
                  backgroundColor: "transparent",
                }}
              />
            )}
            {userData?.username}
          </p>
          <div style={{ display: "flex", flexDirection: "row", fontSize: 15 }}>
            <p
              className="profileMetricLabel"
              style={{ color: "#FFFFFF", marginRight: "1rem" }}
            >
              ⭐ {userData?.starCoins ? userData.starCoins : 0}
              <br />
              <span style={{ fontWeight: 500, fontFamily: "Oswald" }}>
                {t("starCoins").toUpperCase()}
              </span>
            </p>
            <p
              className="profileMetricLabel"
              style={{ color: "#FFFFFF", marginRight: "1rem" }}
            >
              🏆 {isPointsLoading ? "…" : totalChampionPoints ?? 0}
              <br />
              <span style={{ fontWeight: 500, fontFamily: "Oswald" }}>
                {t("championPoints").toUpperCase()}
              </span>
            </p>
            <p
              className="profileMetricLabel"
              style={{ color: "#FFFFFF", marginRight: "1rem" }}
            >
              🎟️ {userData?.tickets ? userData.tickets : 0}
              <br />
              <span style={{ fontWeight: 500, fontFamily: "Oswald" }}>
                TICKETS
              </span>
            </p>
            <p className="profileMetricLabel" style={{ color: "#FFFFFF" }}>
              🏅 {isRankLoading ? "…" : userRank || "—"}
              <br />
              <span style={{ fontWeight: 500, fontFamily: "Oswald" }}>
                RANK
              </span>
            </p>
          </div>
        </div>
      </div>
      {/* <img
        alt="bannerImage"
        src={userData?.banner}
        style={{width: '100%', borderRadius: 20}}
      />
      <div onClick={() => navigate('/account/profile')} id='accountBanner'>
        <div style={{textAlign: 'center'}}>
          <img
            id='accountBannerProfilePicture'
            alt="achievementImage"
            src={userData?.profilePicture ? userData.profilePicture : 'https://res.cloudinary.com/da3qtg7t5/image/upload/v1683238965/Swift/Default_pfp.svg_ufwfdc.png'}
          />
          <p id='accountBannerUsername'>
            {userData?.username}
          </p>
        </div>
        <div id='accountBannerPointsContainer'>
          <p style={{marginBottom: '0.5rem'}}>
            ⭐ 0
          </p>
          <p style={{marginBottom: '0.5rem'}}>
            🏆 0
          </p>
          <p>
            🎟️ 0
          </p>
        </div>
      </div> */}
      <div
        onClick={() => navigate("/account/profile")}
        className="accountPageMenuItem"
      >
        <p>{t("profile").toUpperCase()}</p>
      </div>
      <div
        onClick={() => navigate("/account/notifications")}
        className="accountPageMenuItem"
      >
        <p>
          {t("notifications").toUpperCase()} <span>{unreadNotifications}</span>
        </p>
      </div>
      <div
        onClick={() => navigate("/account/challenges")}
        className="accountPageMenuItem"
      >
        <p>
          {t("challenges").toUpperCase()}{" "}
          <span>{challenges?.length ? challenges.length : 0}</span>
        </p>
      </div>
      <div
        onClick={() => navigate("/account/friends")}
        className="accountPageMenuItem"
      >
        <p>
          {t("friends").toUpperCase()}{" "}
          <span>{friends?.length ? friends?.length : 0}</span>
        </p>
      </div>
      <div
        onClick={() => navigate("/account/champions-road")}
        className="accountPageMenuItem"
      >
        <p>
          {t("championsRoad").toUpperCase()} <span>{userChampionTier}</span>
        </p>
      </div>
      <div
        onClick={() => navigate("/account/achievements")}
        className="accountPageMenuItem"
      >
        <p>
          {t("achievements").toUpperCase()} <span>{achievementsUnlocked}</span>
        </p>
      </div>
      <div
        onClick={() => navigate("/account/wallet")}
        className="accountPageMenuItem"
      >
        <p>
          {t("walletHistory")
            ? t("walletHistory").toUpperCase()
            : "WALLET HISTORY"}
        </p>
      </div>
      <Button
        id="accountPageLogoutBtn"
        onClick={logoutUser}
        className="primaryBtn"
        style={{ marginTop: "1.5rem" }}
      >
        {t("signOut").toUpperCase()}
      </Button>
    </div>
  );
}

export default AccountPage;
